import {ViewStore} from '../base/view-store';
import {action, makeObservable, observable} from 'mobx';
import {ApiRepository} from '../base/api-repository';
import {LabelStore} from '../base/label-store';
import {UserStore} from '../user/user-store';
import {CategoryCard, CommentWithTranslation, DashboardPageTab} from './team-results-store';
import {routes} from '../base/routes';
import {RouterStore} from 'mobx-router';
import {RootStore} from '../base/root-store';
import {periodIdFromRange} from '../base/dateUtils';
import {DateRange} from 'react-day-picker';
import {CategoriesStore} from '../categories/categories-store';
import {Category} from '../questions/questions-store';
import {translateComment} from '../questions/questionUtil';

export type OnboardingHelpModal = 'onboarding-help-company' | 'onboarding-help-feedback' | 'onboarding-help-category';

export class OnboardingStore extends ViewStore {
    @observable
    payload?: OnboardingStatisticsPayload = undefined;
    @observable
    categories?: Category[] = undefined;
    @observable
    variables: OnboardingVariables = new OnboardingVariables();
    @observable
    translateCommentsEnabled: boolean = false;
    @observable
    isTranslationButtonLoading: boolean = false;

    constructor(
        private api: ApiRepository,
        labelStore: LabelStore,
        private userStore: UserStore,
        private categoriesStore: CategoriesStore,
        private router: RouterStore<RootStore>,
    ) {
        super(labelStore);
        makeObservable(this);
    }

    @action
    onEnter(): void {
        this.variables.openTab = 'new-hires';
        this.load(async () => {
            await this.refreshPayload();
            const {from, to} = this.payload!.onboardingTooltipPayload.period;

            this.variables.periodId = periodIdFromRange({from: new Date(from), to: new Date(to)} as DateRange);
        });
    }

    @action
    onDashboardTabChange(openTab: DashboardPageTab): void {
        this.variables.openTab = openTab;
        switch (openTab) {
            case 'team-results': this.router.goTo(routes.dashboard);
                break;
            case 'my-results': this.router.goTo(routes.myDashboard);
                break;
        }
    }

    @action
    setCustomDateSelected(selected: boolean): void {
        this.variables.isCustomDate = selected;
    }

    @action
    onBusinessUnitChange(businessUnitId: string): void {
        this.variables.businessUnitId = businessUnitId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onCompareToChange(compareToBusinessUnitId: string): void {
        this.variables.compareToBusinessUnitId = compareToBusinessUnitId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onLocationChange(locationId: string): void {
        this.variables.locationChoice = locationId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onCategoryChange(categoryId: string): void {
        this.variables.categoryId = categoryId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    openCompanyHelpModal(): void{
        this.variables.openModal = 'onboarding-help-company';
    }

    @action
    openFeedbackHelpModal(): void{
        this.variables.openModal = 'onboarding-help-feedback';
    }

    @action
    openCategoryHelpModal(): void{
        this.variables.openModal = 'onboarding-help-category';
    }

    @action
    closeModal(): void {
        this.variables.openModal = undefined;
    }

    @action
    onPeriodChange(periodId: string): void {
        this.variables.periodId = periodId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    async onTranslateCommentsButtonClicked(): Promise<void> {
        if (!this.translateCommentsEnabled) {
            this.setIsTranslationButtonLoading(true);
            try {
                await this.api.post('/translation/comments', {
                    questionIds: this.getQuestionIdsToTranslateComments(),
                    language: this.userStore.payload!.language,
                    periodId: this.variables.periodId,
                });
                await this.refreshPayload();
            } finally {
                this.setIsTranslationButtonLoading(false);
            }
        }
        this.toggleTranslateComments();
    }

    @action
    toggleTranslateComments(): void {
        this.translateCommentsEnabled = !this.translateCommentsEnabled;
    }

    timeframeFilterLabels(filterOption: string): string {
        if (filterOption === '7') {
            return this.label('common.filter.period.current-week');
        } else {
            return this.label('common.filter.period.past-month');
        }
    }

    translateComment(comment: CommentWithTranslation): string {
        return this.translateCommentsEnabled ? translateComment(comment, this.userStore.payload?.language) : comment.text;
    }

    async refreshPayload(): Promise <void> {
        const queryParameters: Record<string, string> = {};

        if (this.variables.businessUnitId !== undefined) {
            queryParameters['business-unit'] = this.variables.businessUnitId;
        }
        if (this.variables.compareToBusinessUnitId !== undefined) {
            queryParameters['compare-to-unit'] = this.variables.compareToBusinessUnitId;
        }
        if (this.variables.periodId !== undefined) {
            queryParameters['period-id'] = this.variables.periodId;
        }
        if (this.variables.locationChoice !== undefined) {
            queryParameters.location = this.variables.locationChoice;
        }
        if (this.variables.categoryId !== undefined) {
            queryParameters['category-id'] = this.variables.categoryId;
        }

        const queryParamsString = new URLSearchParams(queryParameters).toString();

        this.payload = await this.api.get<OnboardingStatisticsPayload>(`/onboarding/employee/${this.userStore.payload!.employeeId}?${queryParamsString}`);
        await this.categoriesStore.refreshPayload();
        this.setCategories();
    }

    @action
    private setIsTranslationButtonLoading(isLoading: boolean): void {
        this.isTranslationButtonLoading = isLoading;
    }

    @action
    private setCategories(): void {
        this.categories = this.categoriesStore.categories;
    }

    private getQuestionIdsToTranslateComments(): string[] {
        return this.payload!.feedbackCardForOnboardingDashboard!.questionFeedback
            .map((questionFeedback) => questionFeedback.onboardingQuestionId);
    }
}

interface OnboardingStatisticsPayload {
    onboardingFilter: OnboardingFilterPayload;
    questionCardsForOnboardingDashboard?: QuestionCardsForOnboardingDashboard;
    feedbackCardForOnboardingDashboard?: FeedbackCardForOnboardingDashboard;
    companyCardForOnboardingDashboard?: CompanyCardForOnboardingDashboard;
    onboardingTooltipPayload: OnboardingTooltipPayload;
    categoryCardForOnboardingDashboard?: CategoryCard
}

interface OnboardingFilterPayload {
    businessUnits: OnboardingFilter[];
    compareTo: OnboardingFilter [];
    periods: OnboardingFilter[];
    locations: OnboardingFilter[];
    categories: OnboardingFilter[];
}

interface OnboardingFilter {
    id: string;
    label?: string;
    selected: boolean
}

interface CompanyCardForOnboardingDashboard {
    rootBusinessUnitData: BusinessUnitDataForOnboardingDashboard
}

export interface BusinessUnitDataForOnboardingDashboard {
    id: string;
    title: string;
    barpoint: OnboardingBarpoint;
    scoreChange: number;
    chartScores: number[];
    focused: boolean;
    children: BusinessUnitDataForOnboardingDashboard[]
}

interface FeedbackCardForOnboardingDashboard {
    questionFeedback: FeedbackToOnboarding[]
}

export interface FeedbackToOnboarding {
    questionText: string;
    onboardingQuestionId: string;
    answers: FeedbackAnswerWithComment[],
    categoryId: string
}

export interface FeedbackAnswerWithComment extends CommentWithTranslation{
    text: string;
    datetime: string;
    answer: string;
    questionId: string;
    questionBusinessUnitName: string;
    textEnglish?: string,
    textEstonian?: string,
    textFinnish?: string,
    textLatvian?: string,
    textLithuanian?: string,
    textRussian?: string,
    textSwedish?: string,
    textPolish?: string,
    textJapanese?: string,
    textVietnamese?: string,
}

interface QuestionCardsForOnboardingDashboard {
    questions: SingleQuestionCard[]
}

export interface SingleQuestionCard {
    questionText: string;
    categoryId?: string;
    barpoint: OnboardingBarpoint;
    chartScores: number[];
    feedbackAnswers: OnboardingAnswerWithComment[]
}

export interface OnboardingAnswerWithComment {
    text: string;
    datetime: string;
    answer: string;
    questionId: string
}

interface OnboardingBarpoint {
    value: Datapoint;
    compareTo: Datapoint
}

export interface Datapoint {
    prepared: number;
    asked: number;
    answered: number;
    scorePercentage: number;
    participationPercentage: number;
}

interface OnboardingTooltipPayload {
    period: Period;
    businessUnitName: string;
    compareToName: string
}

export interface Period {
    from: string;
    to: string;
}

class OnboardingVariables {
    @observable
    openTab?: DashboardPageTab = undefined;
    @observable
    businessUnitId?: string = undefined;
    @observable
    compareToBusinessUnitId?: string = undefined;
    @observable
    locationChoice?: string = undefined;
    @observable
    openModal?: OnboardingHelpModal = undefined;
    @observable
    periodId?: string = undefined;
    @observable
    isCustomDate: boolean = false;
    @observable
    categoryId?: string = undefined;

    constructor() {
        makeObservable(this);
    }
}
